import React from 'react';
import {
  DollarSign, TrendingDown, Calendar, FileText,
  Save, Printer, ChevronRight,
} from 'lucide-react';
import { QuoteState, MultiCarrierResult, deviceMrcCalc, deviceOneTimeCalc, promoMonthlyCalc, promoOnetimeCalc } from '../types';

interface Props {
  quote:           QuoteState;
  currentSolution?: MultiCarrierResult;
  onSave:          () => void;
  onPrint:         () => void;
  saving:          boolean;
}

const Stat: React.FC<{ label: string; value: string; sub?: string; accent?: boolean; green?: boolean }> = ({
  label, value, sub, accent, green,
}) => (
  <div className={`p-4 rounded-xl ${accent ? 'bg-purple-600/15 border border-purple-500/20' : green ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-white/5 border border-white/8'}`}>
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    <div className={`text-xl font-black ${accent ? 'text-purple-300' : green ? 'text-emerald-400' : 'text-white'}`}>
      {value}
    </div>
    {sub && <div className="text-[10px] text-gray-600 mt-0.5">{sub}</div>}
  </div>
);

export const SummaryPanel: React.FC<Props> = ({ quote, currentSolution, onSave, onPrint, saving }) => {
  const baseMrc     = quote.items.reduce((sum, it) => sum + it.mrc * it.lines, 0);
  // Device MRC: (cost × qty) ÷ financingTerm months; FRP = full cost unspread
  const deviceMrc   = quote.items.reduce(
    (sum, it) => sum + it.devices.reduce((s, d) => s + deviceMrcCalc(d), 0), 0
  );
  // Promo monthly: (value × lines) ÷ duration
  const monthlyDisc = quote.promos.reduce((s, p) => s + promoMonthlyCalc(p), 0);
  // Promo one-time: value × lines
  const onetimeDisc = quote.promos.reduce((s, p) => s + promoOnetimeCalc(p), 0);
  const netMrc      = Math.max(0, baseMrc + deviceMrc - monthlyDisc);
  const deviceOneTime = quote.items.reduce(
    (sum, it) => sum + it.devices.reduce((s, d) => s + deviceOneTimeCalc(d), 0), 0
  );
  const termTotal   = (netMrc * quote.term) + deviceOneTime - onetimeDisc;

  const currentTotal = currentSolution
    ? currentSolution.combined.monthlyServiceTotal + currentSolution.combined.monthlyHardwareTotal + currentSolution.combined.taxesAndFees
    : 0;
  const monthlySavings = currentTotal > 0 ? currentTotal - netMrc : 0;
  const annualSavings  = monthlySavings * 12;

  const lineCount   = quote.items.filter(i => i.type === 'Voice' || i.type === 'Tablet / Mobile Data').reduce((s, i) => s + i.lines, 0);
  const perLine     = lineCount > 0 ? netMrc / lineCount : 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="p-4 rounded-2xl bg-gradient-to-br from-purple-900/40 to-violet-900/20 border border-purple-500/20">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign size={16} className="text-purple-400" />
          <h3 className="font-bold text-white text-sm">Quote Summary</h3>
        </div>

        {/* Main MRC */}
        <div className="text-center py-4 border-b border-white/8 mb-4">
          <div className="text-4xl font-black text-white">
            ${netMrc.toFixed(2)}
            <span className="text-base font-medium text-gray-500">/mo</span>
          </div>
          {monthlyDisc > 0 && (
            <div className="text-sm text-emerald-400 font-medium mt-1">
              Saving ${monthlyDisc.toFixed(2)}/mo in promos
            </div>
          )}
          {quote.customerName && (
            <div className="text-xs text-gray-500 mt-1">{quote.customerName} · {quote.term}-month agreement</div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <Stat label="Service MRC"   value={`$${baseMrc.toFixed(2)}`} />
          <Stat label="Devices MRC"   value={`$${deviceMrc.toFixed(2)}`} />
          <Stat label="Monthly Promos" value={`-$${monthlyDisc.toFixed(2)}`} green={monthlyDisc > 0} />
          <Stat label="One-time Disc"  value={`-$${onetimeDisc.toFixed(2)}`} green={onetimeDisc > 0} />
          {deviceOneTime > 0 && (
            <Stat label="Upfront Devices" value={`$${deviceOneTime.toFixed(2)}`} />
          )}
        </div>

        {lineCount > 0 && (
          <div className="text-center text-xs text-gray-600 mb-2">
            ${perLine.toFixed(2)} per line ({lineCount} lines)
          </div>
        )}

        <Stat
          label={`${quote.term}-Month Agreement Total`}
          value={`$${termTotal.toFixed(2)}`}
          sub={onetimeDisc > 0 ? `After $${onetimeDisc.toFixed(2)} one-time discount` : undefined}
          accent
        />
      </div>

      {/* Savings vs current */}
      {currentSolution && monthlySavings > 0 && (
        <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown size={16} className="text-emerald-400" />
            <span className="font-bold text-emerald-400 text-sm">
              Savings vs. {currentSolution.carriers.length > 1 ? 'All Carriers' : currentSolution.carriers[0]?.providerName || 'Current'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs text-gray-500">Monthly</div>
              <div className="text-lg font-black text-emerald-400">+${monthlySavings.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Annual</div>
              <div className="text-lg font-black text-emerald-400">+${annualSavings.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Term breakdown */}
      {quote.term > 0 && (
        <div className="p-4 rounded-2xl bg-white/5 border border-white/8 space-y-2">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 mb-2">
            <Calendar size={14} /> Term Breakdown
          </div>
          {[1, 12, 24, quote.term].filter((v, i, a) => a.indexOf(v) === i && v > 0).map(mo => (
            <div key={mo} className={`flex items-center justify-between text-sm ${mo === quote.term ? 'text-white font-bold' : 'text-gray-500'}`}>
              <span>{mo} month{mo !== 1 ? 's' : ''}</span>
              <span>${((netMrc * mo) + deviceOneTime - onetimeDisc).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Items breakdown */}
      {quote.items.length > 0 && (
        <div className="p-4 rounded-2xl bg-white/5 border border-white/8">
          <div className="text-xs font-semibold text-gray-400 mb-3">Line Items</div>
          <div className="space-y-2">
            {quote.items.map(item => (
              <div key={item.id} className="flex items-center justify-between text-xs">
                <div className="text-gray-400">
                  {item.planName}
                  <span className="text-gray-600 ml-1">×{item.lines}</span>
                </div>
                <span className="text-white font-medium">${(item.mrc * item.lines).toFixed(2)}/mo</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-2">
        <button
          onClick={onSave}
          disabled={saving || !quote.customerName}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm transition-all duration-200 shadow-lg shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Quote'}
        </button>
        <button
          onClick={onPrint}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-white/10 hover:border-purple-500/40 hover:bg-white/5 text-gray-300 font-semibold text-sm transition-all duration-200"
        >
          <FileText size={16} /> Preview &amp; Print
          <ChevronRight size={14} className="ml-auto" />
        </button>
      </div>

      {/* Quote meta */}
      {quote.sellerInfo.name && (
        <div className="p-3 rounded-xl bg-white/3 border border-white/5">
          <div className="text-xs text-gray-600">Prepared by</div>
          <div className="text-xs text-gray-400 font-medium">{quote.sellerInfo.name}</div>
          {quote.sellerInfo.orgName && <div className="text-xs text-gray-600">{quote.sellerInfo.orgName}</div>}
        </div>
      )}
    </div>
  );
};
